import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { asTags, isValidLink, normalizeIdentifier, DEFAULT_CURRENCY, MAX_IMAGES, MAX_IMAGE_BYTES } from '../fieldTypes';
import { mediaUrl, uploadMedia } from '../config/api';
import { fold } from '../locations';
import { uiLang, useVocabulary } from '../kinds';
import { Button, IconButton } from '../ui';
import { CameraIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, ImageIcon, StarIcon } from '../ui/icons';

/** Free words as chips: Enter or a comma adds one, × removes it, Backspace on an empty input removes the last. */
export function TagsInput({ value, onChange, invalid, inputId, labelledBy }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const tags = asTags(value);
  const add = (text) => {
    const words = text.split(/[,;|\n]+/).map(s => s.trim()).filter(Boolean);
    if (words.length === 0) return;
    const next = [...tags];
    words.forEach(word => {
      if (!next.some(tag => tag.toLowerCase() === word.toLowerCase())) next.push(word);
    });
    onChange(next);
    setDraft('');
  };
  return (
    <div className="tags-input">
      {tags.length > 0 && (
        <ul className="tag-list tags-input__chips">
          {tags.map((tag, index) => (
            <li key={`${tag}-${index}`}>
              <button type="button" className="ui-chip ui-chip--removable" onClick={() => onChange(tags.filter((_, i) => i !== index))}
                aria-label={t('fieldInput.removeTag', { tag })}>
                <span className="ui-chip__value">{tag}</span>
                <span aria-hidden="true" className="tags-input__x">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        id={inputId}
        type="text"
        value={draft}
        aria-labelledby={labelledBy}
        aria-invalid={invalid || undefined}
        placeholder={t('fieldInput.tagsPlaceholder')}
        onChange={(e) => {
          const text = e.target.value;
          if (/[,;|]/.test(text)) add(text); else setDraft(text);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add(draft);
          } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
            onChange(tags.slice(0, -1));
          }
        }}
        onBlur={() => add(draft)}
      />
    </div>
  );
}

/**
 * Codes of a fixed vocabulary (genres) as chips showing their labels in the
 * UI language; typing suggests the vocabulary's labels, Enter or a click
 * adds the first match. Only codes of the list can be added.
 */
export function VocabInput({ value, onChange, vocabulary, invalid, inputId, labels: ownLabels, labelledBy }) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n);
  const items = useVocabulary(vocabulary);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);   // the highlighted suggestion
  const codes = Array.isArray(value) ? value : [];
  // The owner's display labels (option_labels) come first, the model's labels still match.
  const own = (code) => (ownLabels && typeof ownLabels[code] === 'string' && ownLabels[code]) || null;
  const labelOf = (code) => {
    if (own(code)) return own(code);
    const entry = items.find(item => item.code === code);
    return entry ? entry.labels[lang] : code;
  };
  const query = fold(draft);
  const matches = items.filter(item => !codes.includes(item.code)
    && (!query || fold(item.labels[lang]).includes(query) || fold(item.labels.fr).includes(query) || fold(item.labels.en).includes(query)
      || (own(item.code) && fold(own(item.code)).includes(query))));
  const add = (code) => {
    if (!code || codes.includes(code)) return;
    onChange([...codes, code]);
    setDraft('');
    setActive(0);
  };
  const listId = `${inputId || vocabulary}-suggestions`;
  const showList = open && matches.length > 0;
  const current = Math.min(active, Math.max(0, matches.length - 1));
  const optionId = (code) => `${listId}-${code}`;
  return (
    <div className="tags-input vocab-input">
      {codes.length > 0 && (
        <ul className="tag-list tags-input__chips">
          {codes.map((code, index) => (
            <li key={code}>
              <button type="button" className="ui-chip ui-chip--removable" onClick={() => onChange(codes.filter((_, i) => i !== index))}
                aria-label={t('fieldInput.removeTag', { tag: labelOf(code) })}>
                <span className="ui-chip__value">{labelOf(code)}</span>
                <span aria-hidden="true" className="tags-input__x">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        id={inputId}
        type="text"
        value={draft}
        role="combobox"
        aria-labelledby={labelledBy}
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList ? optionId(matches[current].code) : undefined}
        aria-autocomplete="list"
        aria-invalid={invalid || undefined}
        placeholder={t('fieldInput.vocabPlaceholder')}
        onChange={(e) => { setDraft(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            if (matches.length) {
              setActive((current + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
            }
          } else if (e.key === 'Enter') {
            if (showList) {
              e.preventDefault();
              add(matches[current].code);
            } else if (draft.trim()) {
              e.preventDefault();
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          } else if (e.key === 'Backspace' && draft === '' && codes.length > 0) {
            onChange(codes.slice(0, -1));
          }
        }}
      />
      {showList && (
        <ul id={listId} role="listbox" className="vocab-input__list">
          {matches.map((item, index) => (
            <li key={item.code} id={optionId(item.code)} role="option" aria-selected={index === current}
              onMouseDown={(e) => { e.preventDefault(); add(item.code); }}
              onMouseEnter={() => setActive(index)}
              className={`vocab-input__option${index === current ? ' is-active' : ''}`}>
              {labelOf(item.code)}
            </li>
          ))}
        </ul>
      )}
      {draft.trim() !== '' && matches.length === 0 && (
        <p className="ui-field__hint">{t('fieldInput.vocabNoMatch')}</p>
      )}
    </div>
  );
}

export function LinkInput({ value, onChange, required, labelledBy }) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);
  const text = value || '';
  const bad = touched && text.trim() !== '' && !isValidLink(text);
  return (
    <div>
      <input type="url" value={text} required={required} placeholder="https://…" aria-invalid={bad || undefined} aria-labelledby={labelledBy}
        onChange={(e) => onChange(e.target.value)} onBlur={() => setTouched(true)} />
      {bad && <p className="ui-field__error">{t('fieldInput.invalidLink')}</p>}
    </div>
  );
}

/** Whole stars from 1 to max; clicking the current value clears it, "0" sets zero. */
export function RatingInput({ value, onChange, max }) {
  const { t } = useTranslation();
  const top = Number(max) === 5 ? 5 : 10;
  const parsed = value === '' || value === null || value === undefined ? null : Number(value);
  const current = Number.isFinite(parsed) ? parsed : null;   // a stored "great" shows as not rated
  return (
    <div className="rating-input" role="group" aria-label={t('fieldInput.ratingLabel', { max: top })}>
      <button type="button" className="rating-input__zero" onClick={() => onChange(current === 0 ? '' : 0)}
        aria-pressed={current === 0}>0</button>
      {Array.from({ length: top }, (_, i) => i + 1).map(n => (
        <button key={n} type="button" onClick={() => onChange(current === n ? '' : n)}
          aria-label={t('fieldInput.ratingValue', { value: n, max: top })} aria-pressed={current === n}
          className={`rating-input__star${current !== null && n <= Math.round(current) ? ' is-on' : ''}`}>
          <StarIcon size={22} />
        </button>
      ))}
      <span className="rating-input__value">
        {current === null ? t('fieldInput.notRated') : `${current}/${top}`}
      </span>
    </div>
  );
}

/** A yes/no switch; untouched, the field has no value. */
export function YesNoInput({ value, onChange }) {
  const { t } = useTranslation();
  const on = value === true;
  return (
    <label className="yesno-input">
      <input type="checkbox" role="switch" checked={on} aria-checked={on}
        onChange={(e) => onChange(e.target.checked)} />
      {value === true ? t('fieldInput.yes') : value === false ? t('fieldInput.no') : t('fieldInput.notSet')}
    </label>
  );
}

const asObject = (value, key) => (value && typeof value === 'object' && !Array.isArray(value) ? value : { [key]: value ?? '' });

export function MoneyInput({ value, onChange, field, required }) {
  const { t } = useTranslation();
  const money = asObject(value, 'amount');
  const currency = money.currency || field.currency || DEFAULT_CURRENCY;
  const set = (patch) => onChange({ amount: money.amount ?? '', currency, ...patch });
  return (
    <div className="input-pair">
      <input type="text" inputMode="decimal" value={money.amount ?? ''} required={required} className="input-pair__main"
        aria-label={t('fieldInput.amount')} onChange={(e) => set({ amount: e.target.value })} />
      <input type="text" value={currency} maxLength={3} className="input-pair__side input-pair__upper"
        aria-label={t('fieldInput.currency')} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} />
    </div>
  );
}

export function MeasureInput({ value, onChange, field, required }) {
  const { t } = useTranslation();
  const units = field.units || [];
  const measure = asObject(value, 'value');
  const unit = measure.unit || units[0] || '';
  // A value saved before the field was a measure ("93 min") is shown as typed, to retype.
  const set = (patch) => onChange({ value: measure.value ?? '', unit, ...patch });
  return (
    <div className="input-pair">
      <input type="text" inputMode="decimal" value={measure.value ?? ''} required={required} className="input-pair__main"
        aria-label={t('fieldInput.measureValue')} onChange={(e) => set({ value: e.target.value })} />
      {units.length > 1 ? (
        <select value={unit} onChange={(e) => set({ unit: e.target.value })} className="input-pair__side" aria-label={t('fieldInput.unit')}>
          {units.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      ) : (
        <span className="input-pair__unit">{unit}</span>
      )}
    </div>
  );
}

/** An identifier with its scheme's hint and live checksum feedback. */
export function IdentifierInput({ value, onChange, field, required, labelledBy }) {
  const { t } = useTranslation();
  const scheme = field.scheme || 'other';
  const text = value === null || value === undefined ? '' : String(value);
  const normalized = text.trim() === '' ? null : normalizeIdentifier(scheme, text);
  return (
    <div>
      <input type="text" value={text} required={required} spellCheck={false} aria-labelledby={labelledBy}
        className="identifier-input"
        placeholder={t(`fieldInput.schemeHint.${scheme}`)} aria-invalid={(text.trim() !== '' && !normalized) || undefined}
        onChange={(e) => onChange(e.target.value)} />
      {text.trim() !== '' && (
        normalized
          ? <p className="ui-field__hint identifier-input__ok">✓ {normalized !== text.trim() ? t('fieldInput.identifierStored', { value: normalized }) : t('fieldInput.identifierValid')}</p>
          : <p className="ui-field__error">{t(`fieldInput.schemeInvalid.${scheme}`)}</p>
      )}
    </div>
  );
}

const asIds = (value) => (Array.isArray(value) ? value.filter(id => Number.isInteger(id) && id > 0) : []);
const looksLikeImage = (file) => file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name);
const isTouchDevice = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;


/**
 * Photos of an item: picked (several at once), dropped, or taken with the
 * phone's camera; each is uploaded at once (with its progress) and the
 * field's value is the list of media ids. The first is the cover when the
 * field has the cover role. onBusy(true|false) while uploads run.
 */
export function ImageInput({ value, onChange, inventoryId, isCover, onBusy }) {
  const { t } = useTranslation();
  const ids = asIds(value);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const [uploads, setUploads] = useState([]);   // {key, name, progress}
  const [errors, setErrors] = useState([]);
  const [dragging, setDragging] = useState(false);
  const pickerRef = useRef(null);
  const cameraRef = useRef(null);
  const counter = useRef(0);
  // Uploads started and not finished, counted synchronously: room is computed from it, not from a render.
  const pending = useRef(0);

  useEffect(() => {
    if (onBusy) onBusy(uploads.length > 0);
  }, [uploads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (next) => {
    idsRef.current = next;
    onChange(next);
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const problems = [];
    let room = MAX_IMAGES - idsRef.current.length - pending.current;
    files.forEach(file => {
      if (!looksLikeImage(file)) {
        problems.push(t('imageInput.notImage', { name: file.name }));
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        problems.push(t('imageInput.tooLarge', { name: file.name }));
        return;
      }
      if (room <= 0) {
        problems.push(t('imageInput.tooMany', { max: MAX_IMAGES }));
        room = -Infinity;   // say it once
        return;
      }
      room -= 1;
      pending.current += 1;
      const key = ++counter.current;
      setUploads(list => [...list, { key, name: file.name, progress: 0 }]);
      uploadMedia(inventoryId, file, (progress) => {
        setUploads(list => list.map(u => (u.key === key ? { ...u, progress } : u)));
      }).then(media => {
        set([...idsRef.current, media.id]);
      }).catch(error => {
        const code = error.response?.data?.code;
        const known = ['invalid_image', 'image_too_large', 'busy', 'rate_limited'].includes(code);
        setErrors(list => [...list, known
          ? t('imageInput.fileError', { name: file.name, error: t(`mediaErrors.${code}`) })
          : t('imageInput.uploadFailed', { name: file.name })]);
      }).finally(() => {
        pending.current -= 1;
        setUploads(list => list.filter(u => u.key !== key));
      });
    });
    // Added to what is shown, so an error of an upload still running is never wiped.
    if (problems.length) setErrors(list => [...list, ...problems]);
  };

  const move = (index, delta) => {
    const next = [...ids];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set(next);
  };

  const pick = (ref) => () => ref.current && ref.current.click();
  const onFiles = (e) => {
    addFiles(e.target.files);
    e.target.value = '';   // the same file may be picked again
  };

  return (
    <div
      className={`photo-input${dragging ? ' is-dragging' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
    >
      {(ids.length > 0 || uploads.length > 0) && (
        <ul className="photo-input__list">
          {ids.map((id, index) => (
            <li key={id} className="photo-input__thumb">
              <a href={mediaUrl(id, 'original')} target="_blank" rel="noopener noreferrer">
                <img src={mediaUrl(id, 'thumb')} alt={t('imageInput.photoNumber', { number: index + 1 })} loading="lazy" />
              </a>
              {isCover && index === 0 && <span className="photo-input__cover">{t('imageInput.cover')}</span>}
              <button type="button" className="photo-input__button photo-input__remove" onClick={() => set(ids.filter(other => other !== id))}
                aria-label={t('imageInput.remove', { number: index + 1 })} title={t('imageInput.remove', { number: index + 1 })}>
                <CloseIcon size={16} />
              </button>
              {ids.length > 1 && (
                <span className="photo-input__moves">
                  <button type="button" className="photo-input__button" onClick={() => move(index, -1)} disabled={index === 0}
                    aria-label={t('imageInput.moveBefore', { number: index + 1 })} title={t('imageInput.moveBefore', { number: index + 1 })}>
                    <ChevronLeftIcon size={16} />
                  </button>
                  <button type="button" className="photo-input__button" onClick={() => move(index, 1)} disabled={index === ids.length - 1}
                    aria-label={t('imageInput.moveAfter', { number: index + 1 })} title={t('imageInput.moveAfter', { number: index + 1 })}>
                    <ChevronRightIcon size={16} />
                  </button>
                </span>
              )}
            </li>
          ))}
          {uploads.map(upload => (
            <li key={`u${upload.key}`} className="photo-input__thumb photo-input__thumb--upload">
              <progress value={upload.progress} max="100" aria-label={t('imageInput.uploading', { name: upload.name })} />
              {upload.progress}%
            </li>
          ))}
        </ul>
      )}
      <div className="photo-input__actions">
        <Button variant="secondary" size="sm" icon={ImageIcon} onClick={pick(pickerRef)} disabled={ids.length >= MAX_IMAGES}>
          {t('imageInput.add')}
        </Button>
        {isTouchDevice() && (
          <Button variant="secondary" size="sm" icon={CameraIcon} onClick={pick(cameraRef)} disabled={ids.length >= MAX_IMAGES}>
            {t('imageInput.camera')}
          </Button>
        )}
        <span className="ui-field__hint">{t('imageInput.dropHint')}</span>
        <input ref={pickerRef} type="file" accept="image/*,.heic,.heif" multiple onChange={onFiles} hidden />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFiles} hidden />
      </div>
      {errors.length > 0 && (
        <div role="alert" className="photo-input__errors">
          <ul>{errors.map((error, i) => <li key={i}>{error}</li>)}</ul>
          <IconButton icon={CloseIcon} variant="ghost" size="sm" onClick={() => setErrors([])} label={t('imageInput.dismissErrors')} />
        </div>
      )}
    </div>
  );
}
